/* The At Apartment lifecycle vocabulary, and where a request actually is in it.
 *
 * Two things were wrong with rendering the raw request status in the Status
 * column (Satya, 2026-09-01):
 *
 *   1. It speaks HQ's language to the At Apartment team. `in_daily` rendered as
 *      "On daily sheet" — a sentence about HQ's working sheet, which the people
 *      reading this grid have never seen.
 *   2. It is STALE. The request status only moves when somebody presses Hand
 *      over, but real progress is recorded PER ITEM. AA-PO-0002 sat on "on
 *      daily sheet" on a day its own tracking popup already showed 57 items
 *      received at HQ on 31 Aug.
 *
 * So the chip names the furthest stage the ITEMS have reached, in the tracking
 * timeline's own words, and says how many are there. It advances on its own as
 * the team works the Dispatch column — nobody has to remember to press
 * anything for it to read true.
 *
 * ⭐⭐ The fulfilment -> stage mapping below is the BACKEND'S, lifted from
 * `_fulfilment_stage` in apartment_procurement_api.py: ordered = sent to
 * vendor, dispatched AND delivered = back at HQ, any non-pending receipt = at
 * the apartment. If that mapping ever changes, change it here too — otherwise
 * the chip and the timeline describe the same PO differently, which is worse
 * than the bug this replaces.
 */

// The nine hops, in order. Index in this array IS the progress comparison, so
// never reorder it without changing what "furthest" means.
export const STAGE_ORDER = [
  'requested',
  'cluster_approved',
  'final_approved',
  'at_hq',
  'sent_to_vendor',
  'received_at_hq',
  'handed_to_at_apartment',
  'dispatched_by_at_apartment',
  'received_at_apartment'
]

export const STAGE_LABEL = {
  requested: 'Requested',
  cluster_approved: 'Cluster approved',
  final_approved: 'Final approved',
  at_hq: 'At Procurement HQ',
  sent_to_vendor: 'Sent to vendor',
  received_at_hq: 'Received by HQ procurement team',
  handed_to_at_apartment: 'Handed to At Apartment',
  dispatched_by_at_apartment: 'Dispatched by At Apartment',
  received_at_apartment: 'Received at apartment',
  rejected: 'Rejected'
}

// Walks warm -> cool -> green as the goods move, so the column reads as a
// progression at a glance and not just as ten unrelated colours. The first four
// keep the exact values STATUS_META has always used for those stages, so
// nothing that is already on screen changes colour.
export const STAGE_COLOR = {
  requested: '#ff9800',
  cluster_approved: '#ff5722',
  final_approved: '#2196f3',
  at_hq: '#3f51b5',
  sent_to_vendor: '#5c6bc0',
  received_at_hq: '#0097a7',
  handed_to_at_apartment: '#9c27b0',
  dispatched_by_at_apartment: '#00897b',
  received_at_apartment: '#4caf50',
  rejected: '#f44336'
}

// A request that has not been submitted has reached no stage at all.
const DRAFT = { stage: null, label: 'Draft', color: '#9e9e9e' }

// Where the REQUEST-level chain says it is. The per-item stages (5, 6, 8, 9)
// are deliberately absent — no request status corresponds to them, which is
// exactly why the chip used to lag.
const STATUS_STAGE = {
  submitted: 'requested',
  cluster_approved: 'cluster_approved',
  final_approved: 'final_approved',
  in_daily: 'at_hq',
  handed_over: 'handed_to_at_apartment',
  closed: 'received_at_apartment'
}

/* Name the receiver at the one stage whose receiver actually varies — stage 9,
 * which is the apartment the goods went to.
 *
 * ⛔⛔ Stage 6 is NOT one of them. It used to read "Received by At Apartment HQ
 * team" on a store order (2026-07-28), and that is simply wrong (Satya,
 * 2026-09-02: "received by at apartment hq team is wrong, it should be received
 * by HQ Procurement Team, then HQ procurement team will hand over to At
 * Apartment team"). Stage 6 is the HQ PROCUREMENT desk taking delivery from the
 * vendor. That happens the same way whoever the goods are ultimately for — the
 * At Apartment team does not appear in the chain until the hand-over at stage 7.
 * The old label collapsed two different teams into one hop and made stage 7
 * look like it had already happened.
 */
export const labelsFor = (request) => {
  const aptName = (request && request.apartment_name) || ''
  const out = { ...STAGE_LABEL }
  if (aptName) out.received_at_apartment = `Received by ${aptName}`
  return out
}

// One line's own furthest stage, or null if it has not started moving.
// Mirrors the backend's fulfilment/receipt -> stage rules; see the header.
export const stageOfLine = (line) => {
  if (!line) return null
  const receipt = line.receipt_status || 'pending'
  if (receipt !== 'pending') return 'received_at_apartment'
  const f = line.fulfilment_status || 'pending'
  if (f === 'delivered' || f === 'dispatched') return 'received_at_hq'
  if (f === 'ordered') return 'sent_to_vendor'
  // `unavailable` is a dead end, not progress — it must never advance the chip.
  return null
}

const rank = (stage) => (stage ? STAGE_ORDER.indexOf(stage) : -1)

/* The whole answer for one request: which stage to show, what to call it, what
 * colour, and — when the items are what pushed it forward — how many of them
 * are actually there.
 *
 * Returns {stage, label, color, count, total}. `count` is null unless the item
 * progress is BOTH what set the stage and not yet true of every line; a chip
 * reading "65 of 65" is noise, and a count on a request-level stage would be
 * a number nobody set.
 */
export const requestChainStage = (request) =>
  request ? STATUS_STAGE[request.status] || null : null

/* ⭐⭐ ONE line's stage — what the merged Status column SHOWS and what its own
 * dropdown SETS (Satya, 2026-09-02: "can we have the status and dispatch same?
 * it is becoming confusing now — they both should represent the same thing").
 *
 * They were two different truths on one row: Status was the REQUEST rolled up
 * ("received by at apartment hq team · 2 of 53") while Dispatch beside it was
 * THAT line ("Sent to vendor"). A row of this grid is a LINE, and the dropdown
 * writes to the line, so the line is what the cell must name.
 *
 * A line that has not started moving is not nowhere — it is sitting wherever
 * the paperwork got to. So: the furthest of the approval chain's stage and the
 * item's own. Same rule `requestStage` uses, minus the roll-up across lines.
 * ⛔ The count lives on in the tracking popup and the PO header, never here —
 * "2 of 53" is a fact about the request and made no sense on one line.
 */
export const lineStage = (request, line) => {
  if (request && request.status === 'rejected') {
    return {
      stage: 'rejected',
      label: STAGE_LABEL.rejected,
      color: STAGE_COLOR.rejected
    }
  }
  const base = requestChainStage(request)
  const own = stageOfLine(line)
  const best = rank(own) > rank(base) ? own : base
  if (!best) return DRAFT
  const label = labelsFor(request)
  return {
    stage: best,
    label: label[best] || best,
    color: STAGE_COLOR[best] || '#9e9e9e'
  }
}

// The cell's text. One place, so the grid cell, its column filter and the
// Excel export can never disagree — the filter matching what the cell SHOWS is
// a rule this grid has been bitten by before.
export const lineStageText = (request, line) => lineStage(request, line).label

export const requestStage = (request) => {
  if (!request) return DRAFT
  const status = request.status
  if (status === 'rejected') {
    return {
      stage: 'rejected',
      label: STAGE_LABEL.rejected,
      color: STAGE_COLOR.rejected,
      count: null,
      total: 0
    }
  }
  const lines = request.lines || []
  const label = labelsFor(request)
  const base = requestChainStage(request)
  if (!base && !lines.length) return DRAFT

  // Furthest of: where the approval chain got to, and where the items got to.
  // Monotonic on purpose — the backend logs each stage at most once and never
  // walks a request backwards, and neither does this.
  let best = base
  lines.forEach((line) => {
    const s = stageOfLine(line)
    if (rank(s) > rank(best)) best = s
  })
  if (!best) return DRAFT

  const fromItems = rank(best) > rank(base)
  const atBest = lines.filter(
    (line) => rank(stageOfLine(line)) >= rank(best)
  ).length
  return {
    stage: best,
    label: label[best] || best,
    color: STAGE_COLOR[best] || '#9e9e9e',
    count: fromItems && lines.length && atBest < lines.length ? atBest : null,
    total: lines.length
  }
}

// The chip's text, count included. One place, so the grid cell, the column
// filter, the Excel export and the PDF header can never show different words
// for the same row — the filter matching what the cell SHOWS is a rule this
// grid has been bitten by before (see the `matches` comment in RequestsTab).
export const requestStageText = (request) => {
  const s = requestStage(request)
  return s.count == null ? s.label : `${s.label} · ${s.count} of ${s.total}`
}

/* The three hops AFTER the goods reach HQ. They are NOT fulfilment values —
 * hand-over is a request-level action and the last two are per-item lifecycle
 * events — so they carry a `stage:` prefix to keep them out of the enum the
 * Dispatch cell normally saves, and the grid routes them to their own calls.
 *
 * They belong in the same dropdown anyway (Satya, 2026-09-01): the At
 * Apartment team reads that cell as "where is this item", and a list that
 * stops at Unavailable tells them their own half of the journey does not
 * exist. HQ's daily sheet has always shown them greyed under "set later in
 * the flow"; here they are the actual controls.
 */
export const STAGE_PREFIX = 'stage:'
export const DOWNSTREAM_STAGES = [
  'handed_to_at_apartment',
  'dispatched_by_at_apartment',
  'received_at_apartment'
]

/* Which of them a row can move to right now, and why not when it cannot.
 * Reachability is the BACKEND's — hand_over needs in_daily, dispatch needs
 * handed_over — so an option is only offered when the call behind it would
 * actually succeed. Everything is open to every signed-in user (the operator
 * roles were retired 2026-08-27); this gates on where the ORDER is, never on
 * who is looking.
 */
export const downstreamOptionsFor = (request) => {
  const status = (request && request.status) || ''
  const label = labelsFor(request)
  const reason = {
    handed_to_at_apartment:
      status === 'in_daily'
        ? ''
        : 'Available once the order is on HQ’s daily sheet',
    dispatched_by_at_apartment:
      status === 'handed_over' ? '' : 'Available once the order is handed over',
    received_at_apartment:
      status === 'handed_over' ? '' : 'Available once the order is handed over'
  }
  return DOWNSTREAM_STAGES.map((stage) => ({
    value: `${STAGE_PREFIX}${stage}`,
    stage,
    label: label[stage] || stage,
    disabled: !!reason[stage],
    reason: reason[stage]
  }))
}

// True when the Dispatch cell's new value is one of the three above rather
// than a fulfilment_status the cell can just save.
export const isStageValue = (value) =>
  typeof value === 'string' && value.startsWith(STAGE_PREFIX)

export const stageOf = (value) =>
  isStageValue(value) ? value.slice(STAGE_PREFIX.length) : null
